import Delete from '@mui/icons-material/Delete.js';
import { IconButton, Paper, Tooltip } from '@mui/material';
import { useStoreState } from 'pullstate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackdropLoader from '../../components/BackdropLoader.jsx';
import { AppStore } from '../../stores/AppStore.js';
import { api } from '../../utils/apiFunctions';
import { deleteLocalArrayObject } from '../../utils/localStorageFunctions.js';
import CheckInDataGrid from './CheckInDataGrid.jsx';
import CheckInIconButton from './CheckInIconButton.jsx';

const CheckIns = (props) => {

    const goals = useStoreState(AppStore, s => s.goals)
    const checkIns = useStoreState(AppStore, s => s.checkIns)
    const openAlert = useStoreState(AppStore, s => s.snackbar.openAlert);
    const [rowsData, setRowsData] = useState([]);
    const navigate = useNavigate();
    const [openLoader, setOpenLoader] = useState(false);
    const [loaderSkeleton, setLoaderSkeleton] = useState(true);
    const [plusIconDisabled, setPlusIconDisabled] = useState(false)

    const getCheckInData = useCallback(async () => {
        const checkinPlayer = checkIns?.filter((checkin) => {
            return checkin.player_id === props.playerId && checkin.status !== 'archived'
        })
        const goalsPlayer = goals?.filter(goal => goal?.people_builder?.player?.id === props.playerId && goal.goal_status !== 'archived' && goal.people_builder.year === props.peopleBuilder.year) || []
        const goalsPBIds = new Set(Array.from(goalsPlayer?.map(goal => goal?.people_builder_id))) ?? []
        const goalsIds = Array.from(goalsPlayer?.map(goal => goal.id))
        let checkinPlayerFiltered

        checkinPlayerFiltered = checkinPlayer?.filter((checkin) => {
            const checkPbId = checkin.check_in_goals.filter(goal => goalsPBIds.has(goal?.goal?.people_builder_id) || goalsIds.includes(goal?.goal_id))
            return checkin.check_in_type === 'monthly-review' && checkPbId.length
        })

        const checkinData = checkinPlayerFiltered?.map(checkin => ({
            id: checkin.id,
            month: checkin.monthly_ref,
            checkinDate: checkin.check_in_date.split('T')[0],
            meeting: checkin.meeting_date ? 'Yes' : 'No',
            goal_comments: checkin.goal_comments,
        }))

        checkinData?.sort((a, b) => new Date(b.checkinDate) - new Date(a.checkinDate))

        setRowsData(checkinData);
    }, [props.playerId, props.title, checkIns, goals])

    const columns = useMemo(() => [
        {
            field: 'month',
            headerName: 'Month',
            headerClassName: 'checkin-header',
            width: 300,
        },
        {
            field: 'checkinDate',
            headerName: 'Check-In Date',
            headerClassName: 'checkin-header',
            width: 400,
        },
        {
            field: 'meeting',
            headerName: '1:1 Meeting?',
            headerClassName: 'checkin-header',
            width: 300,
            flex: 1
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerClassName: 'checkin-header',
            width: 100,
            renderCell: (params) => {
                return (
                    <Tooltip title="Delete Check-In">
                        <IconButton onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(params.id)
                        }}>
                            <Delete />
                        </IconButton>
                    </Tooltip >
                )
            }
        }
    ], [])

    const getChecksInPlayer = useCallback(async () => {
        const checkinsStored = AppStore.getRawState().checkIns
        const checkinPlayer = checkinsStored?.filter((checkin) => {
            return checkin.player_id === props.playerId && checkin.status !== 'archived'
        })
        return checkinPlayer
    }, [])


    const getTheMostRecentCheckIn = useCallback(async () => {
        const checkinsPlayer = await getChecksInPlayer()
        const latestCheckIn = rowsData?.reduce((latest, current) => {
            return new Date(current.checkinDate) > new Date(latest.checkinDate) ? current : latest;
        }, checkinsPlayer[0]);

        return latestCheckIn;
    }, [])

    const updateLatestCheckInGoal = useCallback(async () => {
        try {
            const latestCheckIn = await getTheMostRecentCheckIn();
            if (!latestCheckIn) return;

            const goalsIds = latestCheckIn.check_in_goals.map(goal => goal.goal_id);
            const goals = AppStore.getRawState().goals;

            await Promise.all(goalsIds.map(async (goalId) => {
                const goal = goals.find(goal => goal.id === goalId);
                if (goal) {
                    await api.post(`/goals/${goalId}`, {
                        last_check_in: latestCheckIn.check_in_date,
                        checkin_status: goal.check_in_goal_status
                    });
                }
            }));

            openAlert('Goals updated successfully', 'success');
        } catch (error) {
            console.error('Error updating goals:', error);
            openAlert('Error updating goals', 'error');
        }
    }, [getTheMostRecentCheckIn, openAlert]);

    const handleDelete = useCallback(async (id) => {

        setOpenLoader(true)
        try {
            const response = await api.delete(`/checkins/${id}`);

            if (response.status === 200) {
                setRowsData(prevData => prevData.filter(row => row.id !== id))
                updateLatestCheckInGoal()
                setOpenLoader(false)
                getCheckInData()
                hasGoals()
                deleteLocalArrayObject('checkIns', id)
            }

            openAlert('Check-in deleted successfully', 'success')
        } catch (error) {
            console.error('Error deleting check-in:', error)
            openAlert('Error deleting check-in', 'error')
            setOpenLoader(false)
        }
    }, [getCheckInData])

    const openCheckInForm = useCallback((id) => {
        const userGoals = goals.filter(goal => goal.people_builder.player.id === id && goal.people_builder_id === props.peopleBuilder.id)
        let userGoalsStatus = new Set()

        if (userGoals.length) {
            userGoals.map(goal => userGoalsStatus.add(goal.goal_status))
        }

        if (userGoalsStatus.has('Draft Goal') || userGoalsStatus.has('Approval') || userGoalsStatus.has('Needs Revision')) {
            openAlert('Goals should be Active', 'error')
            return
        } else if (!userGoalsStatus.size) {
            openAlert('All goals need to be Active', 'error')
            return
        }
        navigate(`/new-check-in/${id}/${props.peopleBuilder.id}`);
    }, [goals])

    const hasGoals = useCallback(() => {
        const userGoals = goals?.filter(goal => goal?.people_builder?.player?.id === props.playerId && goal.goal_status !== 'archived' && goal.people_builder.year === props.peopleBuilder.year)
        const userStatus = userGoals?.map(goal => goal.goal_status)

        if (!userGoals?.length) {
            setPlusIconDisabled(true)
        } else if (!userStatus.every(status => status === 'Active')) {
            setPlusIconDisabled(true)
        } else {
            setPlusIconDisabled(false)
        }
    }, [goals])

    const openRefCheckInForm = useCallback((id) => {
        navigate(`/check-in/${props.playerId}/${id}/${props.peopleBuilder.id}`);
    }, [navigate])

    useEffect(() => {
        getCheckInData()
        hasGoals()
        setTimeout(() => {
            setLoaderSkeleton(false)
        }, 2000);
    }, [])

    useEffect(() => {
        getCheckInData()
    }, [checkIns])

    useEffect(() => {
        hasGoals()
    }, [goals])

    return (
        <React.Fragment>
            <BackdropLoader openBackdrop={openLoader} />
            <Paper sx={{ p: 2, m: 2 }}>
                <CheckInDataGrid
                    rowsData={rowsData}
                    columns={columns}
                    openRefCheckInForm={openRefCheckInForm}
                    loaderSkeleton={loaderSkeleton}
                />
                <CheckInIconButton
                    plusIconDisabled={plusIconDisabled}
                    openCheckInForm={openCheckInForm}
                    playerId={props.playerId}
                />
            </Paper>
        </React.Fragment >
    )
}

export default React.memo(CheckIns)